import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  createStyles,
  makeStyles,
  TextField,
  useTheme
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { Candidate } from './../../../../../search/Candidate';
import AswSearch from './../../../../../search/Icons/AswSearch';
import { PointType } from '../../../../../../states/common';
import Place from '../../../../../../services/google/Place';

import {
  getClosedStationDetail,
  getClosedStationLight
} from '../../../../../../services/ekispert/api';
import {
  getClosedGeocode,
  getClosedPlaceDetail
} from '../../../../../../services/google/api';
import {
  convertLocationToStr,
  GeoLocation
} from '../../../../../../utils/geoLocation';
import { convertLetter } from '../../common/Address';
import { getClosedFixAutocomplete } from '../../../../../../services/google/fixAutocompleteInput';
import { updateErrorMessage } from '../../../../../../actions/generalAction';
import { useDispatch } from 'react-redux';
import { changeLang, Lang } from '../../../../../../states/query';
import History from '../../../../../search/CandidatesModal/Histories/history';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      height: '0px',
    },
    // 出発地アイコン用のスタイル設定。
    departureIcon: {
      fontSize: 18,
    },
    publicDepartureIcon: {
      fontSize: 28,
    },
    // 目的地アイコン用のスタイル設定。
    destinationIcon: {
      fontSize: 20,
    },
    publicDestinationIcon: {
      fontSize: 28,
    },
    textField: {
      backgroundColor: '#ffffff',
      width: '80%',
      position: 'relative',
      top: '10px',
      left: '10%',
      zIndex: 1,
    },
    inputText: {
      borderRadius: '10px',
      height: '50px',
      boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.25)',
      fontSize: '18px'
    },
    pointNameText: {
      whiteSpace: 'nowrap'
    },
    descriptionText: {
      paddingLeft: '10px',
      color: '#999999',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  }),
);

interface Props {
  pointName: string,
  setCenter: (v: {
    lat: number;
    lng: number;
  }) => void,
  setMarkerPoint: (v: {
    lat: number;
    lng: number;
  }) => void,
  setPointName: (v: string) => void,
  setMunicipality: (v: string) => void,
  switchEdited: (v: boolean) => void,
  setPlace: (v: Place) => void,
  setPlaceJP: (v: Place|null) => void,
}

/** Google Mapsの地図上に表示する検索窓のコンポーネント。 */
export const SearchBox: React.FC<Props> = (props: Props) => {
  // 候補のフック。
  const [candidates, setCandidates] = React.useState<Candidate[]> ([]);
  const { t, i18n } = useTranslation();
  // インクリメンタルサーチの候補を表示するか
  const [isDisplayCandidates, switchDisplayCandidates] = React.useState(false);
  const [lang, setLang] = React.useState<Lang> (Lang.ja);

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();

  React.useEffect(() => {
    setLang(changeLang(i18n.language))
  }, [i18n.language])

  /** テキストボックスに文字を入力した際に呼ばれる */
  const onChangeTextBox = (e: any) => {
    if(e.target.value.type !== PointType.SELECT_ON_MAP) {
      getCandidates(e.target.value);
    }
    // 文字が入力された際にはプルダウンメニューを表示する
    switchDisplayCandidates(true);
  }

  /** 候補の初期値を展開する。 */
  const initCandidates = () => {
    /** 「現在地」の項目。 */
    const currentPosition = [
      new Candidate(t('now_place'), '', PointType.CURRENT_POSITION)
    ]
    // 初期値として「現在地」のみ候補を格納する。
    setCandidates(currentPosition);
  }

  /** テキストボックスをクリックしたときの処理。 */
  const onClick = (e: any) => {
    // 候補が空の場合には初期値を格納する。
    // または、テキストボックスが未入力（undefined）の場合も初期値を格納する。
    if (candidates.length === 0 || e.target.value === undefined) {
      initCandidates();
    } else if (e.target.value.length > 0) {
      getCandidates(e.target.value);
    }
    // クリックした際に候補を表示する。
    switchDisplayCandidates(true);
  }

  /**
   * 候補を取得する処理。
   */
  const getCandidates = async (value: string) => {
    let candidates: Candidate[] = [];
    // 入力ボックスの値が空の場合は駅情報を取得しない。
    if (value && value !== '') {
      // 駅すぱあとWebサービスから駅の候補を取得する。
      const stations = await getClosedStationLight(value, i18n.language);
      // Google Places APIから地点の候補を取得する。
      const places = await getClosedFixAutocomplete(value, i18n.language);
      if(places) {
        // 駅と地点の候補をひとつの配列にする。
        candidates = candidates.concat(stations, places);
      }
    }
    // 種別ごとのソートを実行する。
    candidates = sortByType(candidates);
    setCandidates(candidates);
    return candidates;
  }

  /**
   * 発着地のサジェスト（候補）を種別ごとにソートする。
   * @param candidates 駅すぱあとAPI（/station/light）から取得した候補。
   */
  const sortByType = (candidates: Candidate[]) => {
    /** 駅の種別ごとにソート済みの駅候補を格納する配列。 */
    let sortedStations: Candidate[] = [];
    // 種別の順序を固定するために、あらかじめキーと初期値を設定する。
    // 順序は「鉄道」「バス」「船」の順。（2020/02/18現在。）
    const stationsByType: { [key: string]: Candidate[] } = {
      'currentPosition': [
        new Candidate(t('now_place'), '', PointType.CURRENT_POSITION)
      ],
      'selectMap': [],
      'train': [],
      'place': [],
      'bus': [],
      'ship': [],
    };
    candidates.forEach(c => {
      // あらかじめ宣言した種別の駅データのみ、各種別ごとに格納する。
      if (Object.keys(stationsByType).includes(c.type)) { stationsByType[c.type].push(c); }
    })
    // 連想配列のキーをループする。
    Object.keys(stationsByType).forEach((key) => {
      const stations = stationsByType[key];
      // 各種別ごとの配列の要素が存在する場合のみ以下の処理を実行する。
      // （ネストが深いのであとで見直す。2020/02/18現在。）
      if (stations && stations.length > 0) {
        // 種別順にならんだ連想配列の値（駅候補の配列）を結合する。
        sortedStations = sortedStations.concat(stations);
      }
    });
    return sortedStations;
  }

  /**
   * 検索ボックスに文字が追加された場合 or 地点を確定した際に呼ばれる
   * @param value 文字追加の場合 string、地点確定の場合 Candidate
   * @returns
   */
  const updateValue = async (value: string | Candidate) => {
    // エラーメッセージを空にする
    dispatch(updateErrorMessage(''));
    // 同じ地点を連続で再選択した場合にラベル部分が更新されない場合があるため、
    // 候補の変更時に一旦空文字を設定する。
    props.setPointName('');
    props.setPlaceJP(null)
    // 中心座標が変更された場合、編集済みフラグをオンにする。
    // （このフラグがオンの場合、利用空港が変更された際、初期位置が変更されない。）
    props.switchEdited(true);
    if (value instanceof Candidate ) {
      // サジェストから「施設（地点）」を選択した場合の処理。
      if (value.type === 'place') {
        /** 選択された地点のID。 */
        const placeId = value.code;
        try {
          /** 選択された地点情報。（緯度経度など） */
          const placeDetail = await getClosedPlaceDetail(placeId, undefined, lang);
          // 地点情報が取得できなかった場合は、以降の処理を実行しない。
          if (!placeDetail) { return; }
          /** 選択された地点の緯度経度。 */
          const coordinates = placeDetail.getLocation();

          // 一旦ジオコーディングAPIからの情報取得は取りやめる (2022/02/09)
          /** ジオコーディングAPIから取得した地点情報。 */
          // const places: Place[] = await getClosedGeocode(`${coordinates.lat},${coordinates.lng}`);
          // places.forEach((x)=>{ console.log(x.getFullAddress()) })
          /** 取得した地点情報のうちの1件目。 */
          // const place2 = places[0];
          // 地点情報が取得できなかった場合は、以降の処理を実行しない。
          // if (!place) { return; }

          const place = placeDetail;
          /** 市区町村までの住所。 */
          const municipality = convertLetter(place.getAddressToMunicipality());
          /** 住所。 */
          const address = convertLetter(place.getFullAddress(changeLang(i18n.language)));
          // 日本語用地点名の更新を
          if(i18n.language !== 'ja') {
            const placeJPDetail = await getClosedPlaceDetail(placeId, undefined, Lang.ja);
            if(placeJPDetail) {
              props.setPlaceJP(placeJPDetail);
            }
          }
          else {
            props.setPlaceJP(place);
          }
          /** 地点名として更新するラベル（住所 + 施設名） */
          const label = `${address} ${value.name}`;
          // 市区町村までの住所を更新する。
          props.setMunicipality(municipality);
          // 選択された地点名を更新する。
          props.setPointName(label);
          // 選択された地点を更新する
          props.setPlace(place);
          // 選択された地点の緯度経度を発着地の地点として更新する。
          props.setCenter(coordinates);
          // 選択された地点の緯度経度をマーカーを表示する座標として更新する。
          props.setMarkerPoint(coordinates);
        }
        catch(e) {
          const errorMessage = "必要な住所情報が取得できませんでした。恐れ入りますが、別の名称で再検索いただくか、詳細場所を地図上のピンで指定してください。";
          dispatch(updateErrorMessage(errorMessage));
          if(process.env['REACT_APP_STAGE_ENV'] === 'dev') {
            console.error(e);
          }
        }
      }
      // サジェストから「駅・バス停・港」のいずれかを選択した場合の処理。
      if (value.type === 'train' || value.type === 'bus' || value.type === 'ship') {
        const code = value.code
        const station = await getClosedStationDetail(code);
        /** 選択された駅・バス停・港の緯度。 */
        const lat = station.GeoPoint.lati_d;
        /** 選択された駅・バス停・港の経度。 */
        const lng = station.GeoPoint.longi_d;
        /** 選択された地点の緯度経度。 */
        const coordinates = {
          lat: Number.parseFloat(lat),
          lng: Number.parseFloat(lng),
        }
        const places: Place[] = await getClosedGeocode(`${lat},${lng}`, lang);
        // 地点情報が取得出来る場合、住所情報とラベルの取得をする
        if (places) {
          const place = places[0];
          /** 市区町村までの住所。 */
          const municipality = convertLetter(place.getAddressToMunicipality());
          // 市区町村までの住所を更新する。
          props.setMunicipality(municipality);
          /** 住所。 */
          const address = convertLetter(place.getFullAddress(lang));
          /** 地点名として更新するラベル（住所 + 施設名） */
          const label = `${address} ${value.name}`;
          // 選択された地点名を更新する。
          props.setPointName(label);
          // 選択された地点を更新する
          props.setPlace(place);
          if(lang !== Lang.ja) {
            const placeJPDetail = await getClosedGeocode(`${lat},${lng}`, Lang.ja);
            if(placeJPDetail[0]) {
              props.setPlaceJP(placeJPDetail[0]);
            }
          }
          else {
            props.setPlaceJP(place);
          }
        }
        // 中心座標更新と共に料金取得リクエストが走るため、最後に中心座標を入れている
        props.setCenter(coordinates);
        props.setMarkerPoint(coordinates);

      }
      // サジェストから「現在地」を選択した場合は、現在地の緯度経度をReduxに格納する。
      if (value.type === PointType.CURRENT_POSITION) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          // 現在地の緯度経度のオブジェクト。
          const geoPoint: GeoLocation = { lat, lng };
          /** 現在地の座標（緯度経度）。 */
          const coordinate = convertLocationToStr(geoPoint);
          /** 現在地の座標から取得した地点情報。 */
          const places: Place[] = await getClosedGeocode(coordinate, lang);
          // 地点情報が取得できなかった場合は、以降の処理を実行しない。
          if (!places) { return; }
          /** 取得した地点情報の中の1件目。 */
          const place = places[0];
          /** 市区町村までの住所。 */
          const municipality = convertLetter(place.getAddressToMunicipality());
          /** 住所。 */
          const address = convertLetter(place.getFullAddress(lang));
          // 料金取得API用に日本語名称を取得する
          if(lang !== Lang.ja) {
            const placesJP: Place[] = await getClosedGeocode(coordinate, Lang.ja);
            if(placesJP[0]) {
              props.setPlaceJP(placesJP[0]);
            }
          }
          else {
            props.setPlaceJP(place);
          }
          // 市区町村までの住所を更新する。
          props.setMunicipality(municipality);
          // 選択された地点名を更新する。
          props.setPointName(address);
          // 選択された地点を更新する
          props.setPlace(place);
          // 中心座標を更新する
          props.setCenter({lat, lng});
          props.setMarkerPoint({lat, lng});
        }, (e) => {
          /** 現在地を取得できない場合 */
          dispatch(updateErrorMessage(t('no_get_now_place')));
          return;
        });
      }
    }
  }

  /** インクリメンタルサーチの候補に表示する文字列 */
  const renderOption = (option: Candidate) => {
    if(option.description) {
      return (
        <>
          <span className={classes.pointNameText}>
            {option.name}
          </span>
          <span className={classes.descriptionText}>
            {option.description}
          </span>
        </>
      )
    }
    return option.name;
  }

  return (
    <Autocomplete
      className={classes.root}
      getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
      filterOptions={x => x}
      onChange={(e, value) => {
        if (value !== null && (typeof value === 'string' || value instanceof Candidate)) {
          updateValue(value);
        }
      }}
      options={ candidates }
      value={props.pointName}
      autoComplete
      includeInputInList
      freeSolo
      debug
      groupBy={(option: Candidate | History) => option.getTypeLabel(t) }
      open={isDisplayCandidates}
      onClose={()=>{
        switchDisplayCandidates(false);
      }}
      renderOption={renderOption}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          className={classes.textField}
          onClick={onClick}
          onChange={onChangeTextBox}
          InputProps={{
            ...params.InputProps,
            className: '',
            endAdornment: (
              <AswSearch/>
            )
          }}
          InputLabelProps={{
            style: {
              color: theme.palette.text.secondary,
            }
          }}
        />
      )}
    />
  );
}

export default SearchBox;
